import React from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface Partner {
  id: number;
  name: string;
  logo: string;
  alt: string;
}

interface Testimonial {
  id: number;
  name: string;
  role: string;
  organization: string;
  quote: string;
  image: string;
  alt: string;
}

interface TrustSignalsProps {
  partners: Partner[];
  testimonials: Testimonial[];
}

const TrustSignals = ({ partners, testimonials }: TrustSignalsProps) => {
  return (
    <section className="py-16 lg:py-24 bg-card">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center space-y-4 mb-12">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-primary/10 rounded-full">
            <Icon name="ShieldCheckIcon" size={20} variant="solid" className="text-primary" />
            <span className="text-sm font-medium text-primary">Trusted by Experts</span>
          </div>
          <h2 className="text-3xl lg:text-5xl font-headline font-semibold text-foreground">
            Endorsed by Leading Organizations
          </h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-16">
          {partners.map((partner) => (
            <div
              key={partner.id}
              className="flex items-center justify-center p-6 bg-background rounded-xl hover:shadow-soft transition-all duration-300"
            >
              <AppImage
                src={partner.logo}
                alt={partner.alt}
                className="w-full h-16 object-contain opacity-70 hover:opacity-100 transition-opacity"
              />
            </div>
          ))}
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {testimonials.map((testimonial) => (
            <div
              key={testimonial.id}
              className="bg-background rounded-2xl p-8 shadow-soft hover:shadow-elevated transition-all duration-300"
            >
              <div className="flex items-start space-x-4 mb-6">
                <div className="relative w-16 h-16 rounded-full overflow-hidden flex-shrink-0">
                  <AppImage
                    src={testimonial.image}
                    alt={testimonial.alt}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1">
                  <h4 className="text-lg font-headline font-semibold text-foreground">
                    {testimonial.name}
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    {testimonial.role}
                  </p>
                  <p className="text-sm text-primary font-medium">
                    {testimonial.organization}
                  </p>
                </div>
                <Icon name="ChatBubbleLeftIcon" size={24} variant="solid" className="text-accent" />
              </div>

              <blockquote className="text-foreground leading-relaxed italic">
                "{testimonial.quote}"
              </blockquote>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TrustSignals;